import { useTranslation } from 'react-i18next';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import Eyebrow from '../atoms/Eyebrow';
import type { Me } from '@/types/api';

// PR-UX-05 JTBD · CTA derived from user state, not from a static menu.
// PR-UX-04 4-states · loading (me=null) / 5 entitlement variants / error (parent passes) / success (CTA fires).
// PR-UX-10 Hick · single primary action per card. No competing CTAs.

type Action = 'start' | 'subscribe' | 'fix_payment';

export interface EntitlementCardProps {
  me: Me | null;
  busy?: boolean;
  onStart: () => void;        // start council (trial or paid)
  onSubscribe: () => void;    // open Lemon Squeezy checkout
  onFixPayment: () => void;   // navigate to billing tab
  error?: string | null;
}

interface CTASpec {
  action: Action;
  label: string;
  note?: string;
}

function buildCTA(me: Me | null, t: (k: string) => string): CTASpec {
  if (!me) return { action: 'start', label: t('dash.cta.start') };
  const s = me.subscription_status;
  if (s === 'active' || s === 'trial') return { action: 'start', label: t('dash.cta.start') };
  if (s === 'canceled') return { action: 'start', label: t('dash.cta.start'), note: t('dash.cta.cancelsNote') };
  if (s === 'past_due') return { action: 'fix_payment', label: t('dash.cta.fixPayment') };
  if (!me.trial_used) return { action: 'start', label: t('dash.cta.startTrial') };
  return { action: 'subscribe', label: t('dash.cta.subscribe') };
}

function bodyCopy(me: Me | null, t: (k: string) => string): string {
  if (!me) return t('dash.bodyLoading');
  const s = me.subscription_status;
  if (s === 'active') return t('dash.body.active');
  if (s === 'trial') return t('dash.body.trial');
  if (s === 'canceled') return t('dash.body.canceled');
  if (s === 'past_due') return t('dash.body.pastDue');
  if (!me.trial_used) return t('dash.body.trialAvailable');
  return t('dash.body.trialUsed');
}

export default function EntitlementCard({
  me,
  busy = false,
  onStart,
  onSubscribe,
  onFixPayment,
  error,
}: EntitlementCardProps) {
  const { t } = useTranslation();
  const cta = buildCTA(me, t);

  const handler =
    cta.action === 'start' ? onStart : cta.action === 'subscribe' ? onSubscribe : onFixPayment;

  return (
    <Card padding="normal" className="max-w-2xl">
      <div className="flex items-baseline justify-between mb-2">
        <Eyebrow tone="ink">{t('dash.actionTitle')}</Eyebrow>
        {me && (
          <Eyebrow tone="ash">
            {t(`dash.status.${me.subscription_status}`)}
          </Eyebrow>
        )}
      </div>
      <p className="text-base text-ink-2 mb-6">{bodyCopy(me, t)}</p>
      <Button
        variant="primary"
        size="lg"
        onClick={handler}
        loading={busy}
        data-testid="entitlement-cta"
      >
        {cta.label}
      </Button>
      {cta.note && <p className="text-sm text-ink-3 mt-3">{cta.note}</p>}
      {error && <p className="text-sm text-vermilion-700 mt-3">{error}</p>}
    </Card>
  );
}
